import {Injectable, Inject} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';
import {AuthenticationService} from '../../auth/services/authentication.service';
import {
  AddGameRequest,
  CoachApplication,
  CreateCategory,
  CreateCourse,
  CreateTeams, News, Pagination,
  UpdateCategory,
  UpdateCourse,
  UpdateTeams, Volunteers
} from "../../shared/SharedEntities";

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  private httpOptions = new BehaviorSubject<any>(this.getDefaultHttpOptions());

  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string, private authenticationService: AuthenticationService) {
    this.authenticationService.currentUser.subscribe(() => {
      this.updateHttpOptions();
    });
  }

  // region users
  getAllUsers(): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'users', this.getDefaultHttpOptions());
  }

  deleteUser(id: string): Observable<any> {
    return this.http.delete<any>(this.baseUrl + 'users/' + id, this.getDefaultHttpOptions());
  }

  updateUser(id: string, model: any): Observable<any> {
    return this.http.put<any>(this.baseUrl + 'users/' + id, model, this.getDefaultHttpOptions());
  }

  createUser(model: any): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'users', model, this.getDefaultHttpOptions());
  }

  // region leagues
  getAllLeagues(page: number = 0, size: number = 10): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}leagues?page=${page}&size=${size}`);
  }

  deleteLeague(id: string): Observable<any> {
    return this.http.delete<any>(this.baseUrl + 'leagues/' + id, this.getDefaultHttpOptions());
  }

  createLeague(league: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}leagues`, league, this.getDefaultHttpOptions());
  }

  updateLeague(id: string, league: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}leagues/${id}`, league, this.getDefaultHttpOptions());
  }

  // region teams
  getAllTeamsFromOneLeague(leagueId: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'teams/league/' + leagueId, this.getDefaultHttpOptions());
  }

  deleteTeamFromOneLeague(teamId: string): Observable<any> {
    return this.http.delete<any>(this.baseUrl + 'teams/' + teamId, this.getDefaultHttpOptions());
  }

  updateTeamFromOneLeague(leagueId: string, model: UpdateTeams): Observable<any> {
    return this.http.put<any>(this.baseUrl + 'teams/' + leagueId, model, this.getDefaultHttpOptions());
  }

  createTeamFromOneLeague(model: CreateTeams): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'teams', model, this.getDefaultHttpOptions());
  }

  uploadTeamLogo(id: string, model: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', model);

    return this.http.post<any>(
      `${this.baseUrl}teams/${id}/logo`,
      formData,
      this.getDefaultImageHttpOptions()
    );
  }

  uploadPdfLogo(id: string, model: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', model);

    return this.http.post<any>(
      `${this.baseUrl}teams/${id}/pdf`,
      formData,
      this.getDefaultImageHttpOptions()
    );
  }

  getTeamById(id: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + `teams/${id}`, this.getDefaultHttpOptions());
  }


  // region categories
  getAllCategories(): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'categories', this.getDefaultHttpOptions());
  }

  getAllCategoriesWithTopLevel(): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'categories/top-level', this.getDefaultHttpOptions());
  }

  deleteCategory(id: string): Observable<any> {
    return this.http.delete<any>(this.baseUrl + 'categories/' + id, this.getDefaultHttpOptions());
  }

  updateCategory(id: string, model: any): Observable<any> {
    return this.http.put<any>(this.baseUrl + 'categories/' + id, model, this.getDefaultHttpOptions());
  }

  updateCategoryOrder(id: string, model: any): Observable<any> {
    return this.http.put<any>(this.baseUrl + `admin/categories/${id}/reorder`, model, this.getDefaultHttpOptions());
  }

  createCategory(model: any): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'categories', model, this.getDefaultHttpOptions());
  }

  // region news
  getAllNews(page: number = 0, size: number = 10, query?: string): Observable<Pagination<News>> {
    let params: any = { page, size };

    if (query) {
      params.query = query;
    }

    return this.http.get<Pagination<News>>(`${this.baseUrl}admin/posts`, { params });
  }

  getNewsById(id: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + `posts/${id}`, this.getDefaultHttpOptions());
  }

  deleteNews(newsId: string): Observable<any> {
    const options = {
      ...this.getDefaultHttpOptions(),
      responseType: 'text' as 'text'
    };
    return this.http.delete(this.baseUrl + `posts/${newsId}`, options);
  }

  updateNews(id: string, model: any): Observable<any> {
    return this.http.put<any>(this.baseUrl + 'posts/' + id, model, this.getDefaultHttpOptions());
  }

  createNews(model: any): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'posts', model, this.getDefaultHttpOptions());
  }

  postPdfImage(id: string, model: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', model);
    return this.http.post(
      `${this.baseUrl}posts/${id}/pdf`,
      formData,
      { ...this.getDefaultImageHttpOptions(), responseType: 'text' }
    );
  }

  postMainImage(id: string, model: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', model);
    return this.http.post(
      `${this.baseUrl}posts/${id}/main-image`,
      formData,
      { ...this.getDefaultImageHttpOptions(), responseType: 'text' }
    );
  }

  postAdditionalImages(postId: string, files: File[]): Observable<any> {
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    return this.http.post<any>(`${this.baseUrl}posts/${postId}/additional-images`, formData, this.getDefaultImageHttpOptions());
  }

  // national games
  getLastThreeGames() {
    return this.http.get<any>(this.baseUrl + 'national-games/next-three-games', this.getDefaultHttpOptions());
  }

  createNationalGame(model: any): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'national-games', model, this.getDefaultHttpOptions());
  }

  insertHomeTeamLogo(id: string, model: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', model);

    return this.http.put<any>(
      `${this.baseUrl}national-games/${id}/home-image`,
      formData,
      this.getDefaultImageHttpOptions()
    );
  }

  insertAwayTeamLogo(id: string, model: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', model);
    return this.http.put<any>(
      `${this.baseUrl}national-games/${id}/away-image`,
      formData,
      this.getDefaultImageHttpOptions()
    );
  }

  updateGame(id: string, model: any): Observable<any> {
    return this.http.put<any>(this.baseUrl + `national-games/${id}`, model, this.getDefaultHttpOptions());
  }

  updateScore(id: string, model: any): Observable<any> {
    return this.http.patch<any>(this.baseUrl + `national-games/${id}/scores`, JSON.stringify(model), this.getDefaultHttpOptions());
  }

  deleteNationalGame(id: string) {
    return this.http.delete(this.baseUrl + `national-games/${id}`, this.getDefaultHttpOptions());
  }

  getWeeksForLeague(leagueId: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'leagues', this.getDefaultHttpOptions());
  }

  getGames(leagueId: string, weekNumber: any): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'leagues', this.getDefaultHttpOptions());
  }

  addGame(game: AddGameRequest): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'leagues', game, this.getDefaultHttpOptions());
  }

  getAllVolunteers(page: number = 0, size: number = 10, fullName?: string, personalNumber?: string): Observable<Pagination<Volunteers>> {
    let params: any = { page, size };

    if (fullName) {
      params.fullName = fullName;
    }
    if (personalNumber) {
      params.personalNumber = personalNumber;
    }

    return this.http.get<Pagination<Volunteers>>(`${this.baseUrl}volunteers`, { params });
  }

  updateVolunteerStatus(id: string, status: string): Observable<any> {
    const url = `${this.baseUrl}volunteers/${id}/status?status=${encodeURIComponent(status)}`;
    return this.http.patch<any>(url, {}, this.getDefaultHttpOptions());
  }

  getAllCoachApplications(page: number = 0, size: number = 10, personalNumber?: string, name?: string): Observable<Pagination<CoachApplication>> {
    let params: any = { page, size };

    if (personalNumber) {
      params.personalNumber = personalNumber;
    }
    if (name) {
      params.name = name;
    }

    return this.http.get<Pagination<CoachApplication>>(`${this.baseUrl}coach-applications`, { params });
  }

  updateCoachApplicationStatus(id: string, status: string, comment?: string): Observable<any> {
    const url = `${this.baseUrl}coach-applications/${id}/status`;
    return this.http.patch<any>(url, {status, comment}, this.getDefaultHttpOptions());
  }

  // region courses
  getAllCourses(): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'courses', this.getDefaultHttpOptions());
  }

  getCourseById(id: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + `courses/${id}`, this.getDefaultHttpOptions());
  }

  createCourse(model: CreateCourse): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'courses', model, this.getDefaultHttpOptions());
  }

  updateCourse(id: string, model: UpdateCourse): Observable<any> {
    return this.http.put<any>(this.baseUrl + `courses/${id}`, model, this.getDefaultHttpOptions());
  }

  deleteCourse(id: string): Observable<any> {
    return this.http.delete<any>(this.baseUrl + `courses/${id}`, this.getDefaultHttpOptions());
  }

  // standings games
  getStandingsForALeague(id: string) {
    return this.http.get<any>(this.baseUrl + 'standings/league/' + id, this.getDefaultHttpOptions());
  }

  getMatches(matchId: any): Observable<any> {
    return this.http.get<any>(this.baseUrl + `matches/${matchId}`, this.getDefaultHttpOptions());
  }

  getWeekMatches(leagueId: string, weekNumber: any): Observable<any> {
    return this.http.get<any>(this.baseUrl + `matches/${leagueId}/week/${weekNumber}`, this.getDefaultHttpOptions());
  }

  addWeekMatches(leagueId: string, weekNumber: any, model: any): Observable<any> {
    return this.http.post<any>(this.baseUrl + `matches/${leagueId}/week/${weekNumber}`, JSON.stringify(model), this.getDefaultHttpOptions());
  }

  updatedWeekMatches(matchId: any, model: any): Observable<any> {
    return this.http.put<any>(this.baseUrl + `matches/${matchId}`, JSON.stringify(model), this.getDefaultHttpOptions());
  }

  deleteWeekMatches(matchId: string): Observable<any> {
    return this.http.delete<any>(this.baseUrl + 'matches/' + matchId, this.getDefaultHttpOptions());
  }

  getWeeksPerLeague(leagueId: string) {
    return this.http.get<any>(this.baseUrl + `matches/${leagueId}/completed-gameweeks`, this.getDefaultHttpOptions());
  }

  // Youtube Videos
  addVideosUrl(model: any): Observable<any> {
    return this.http.post<any>(this.baseUrl + `videos`, JSON.stringify(model), this.getDefaultHttpOptions());
  }

  updateVideosUrl(id: string, model: any): Observable<any> {
    return this.http.put<any>(this.baseUrl + `videos/` + id, JSON.stringify(model), this.getDefaultHttpOptions());
  }

  getVideosUrl() {
    return this.http.get<any>(this.baseUrl + `videos/latest`, this.getDefaultHttpOptions());
  }

  deleteVideo(id: string): Observable<any> {
    return this.http.delete<any>(this.baseUrl + 'videos/' + id, this.getDefaultHttpOptions());
  }

  private updateHttpOptions(): void {
    this.httpOptions.next(this.getDefaultHttpOptions());
  }

  private getDefaultHttpOptions(): any {
    const currentUser = this.authenticationService.currentUserValue;
    return {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        "Authorization": currentUser ? "Bearer " + currentUser.token : ""
      })
    };
  }

  private getDefaultImageHttpOptions(): any {
    const currentUser = this.authenticationService.currentUserValue;
    return {
      headers: new HttpHeaders({
        "Authorization": currentUser ? "Bearer " + currentUser.token : ""
      })
    };
  }
}
